import { type ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  ExternalConfigContext,
  useExternalConfig,
} from '../../../../core/components/overlay-tooltip/contexts/ExternalConfigContext.js';
import { ChartTooltip } from '../../../../core/components/overlay-tooltip/OverlayTooltip.js';
import type { SeriesActionsTemplate } from '../../../../core/types/series-actions-template.js';
import { FLAME_TOOLTIP_MESSAGES } from './messages.js';

const FLAME_TOOLTIP_PREFIX = ''; //We don't want X-value labels from XYChart default tooltip

interface FlameTooltipProps {
  label: string;
  subheader?: string;
  color: string;
  valueLabel: string;
  totalValue: string;
  totalRelativeValue?: string;
  selfValue?: string;
  selfRelativeValue?: string;
  actions?: () => SeriesActionsTemplate;
  /**
   * Chart-scoped actions rendered inside the tooltip footer, independent of
   * the hovered series row. The tooltip only renders the footer; it does not
   * know what the actions do. Empty or `undefined` hides the footer.
   */
  globalActions?: ReactNode[];
}

export function FlameTooltip({
  label,
  subheader,
  color,
  valueLabel,
  totalValue,
  totalRelativeValue,
  selfValue,
  selfRelativeValue,
  actions,
  globalActions,
}: Readonly<FlameTooltipProps>): ReactNode {
  const intl = useIntl();
  const externalConfig = useExternalConfig();

  const contextValue = useMemo(
    () => ({
      ...externalConfig,
      symbolPosition: 'left' as const,
      displayHeaderDivider: true,
    }),
    [externalConfig],
  );

  return (
    <ExternalConfigContext.Provider value={contextValue}>
      <ChartTooltip.Header
        header1={label}
        label={FLAME_TOOLTIP_PREFIX}
        subheader={subheader}
      />
      <ChartTooltip.SeriesHeader
        seriesLabel={intl.formatMessage(FLAME_TOOLTIP_MESSAGES.values)}
        valueLabel={valueLabel}
      />
      <ChartTooltip.SeriesItem
        color={color}
        name={intl.formatMessage(FLAME_TOOLTIP_MESSAGES.total)}
        value={totalValue}
        relativeValue={totalRelativeValue}
        actions={actions}
      />
      {selfValue !== undefined && (
        <ChartTooltip.SeriesItem
          color={color}
          name={intl.formatMessage(FLAME_TOOLTIP_MESSAGES.self)}
          value={selfValue}
          relativeValue={selfRelativeValue}
          actions={actions}
        />
      )}
      {globalActions !== undefined && globalActions.length > 0 && (
        <ChartTooltip.Footer
          actions={globalActions}
          unpinnedPlaceholderText={undefined}
        />
      )}
    </ExternalConfigContext.Provider>
  );
}
